import { useEffect, useRef, type RefObject } from "react";

/**
 * Drag-to-dismiss for the bottom sheet (`Sheet dragToDismiss`) — internal to the primitive, not exported. Ported from
 * Antenna's legacy checkout sheet (`sheet-swipe 2026-07-12`): TOUCH only, armed only at a touchstart that lands while
 * the panel's own scroll is at its top (so scrolling a long body never fights the gesture), following the finger 1:1
 * DOWNWARD, and dismissing on release only when the drag EXCEEDED `distance` (no velocity, no exit animation — the
 * caller's `onClose` unmounts or closes it where it was dropped). Anything shorter settles back.
 *
 * Touch Events, never Pointer Events: the panel is a scroll container, and a pointer drag on one is cancelled the
 * moment the browser claims the pan unless the panel sets `touch-action: none` — which would stop its body scrolling.
 * The listeners are passive (the pull-to-refresh precedent): the drag never prevents a scroll.
 *
 * The panel is written IMPERATIVELY (`style.transform` + `data-drag`), not through React state, so a drag re-renders
 * nothing; `data-drag="dragging" | "settling"` is what the primitive's stylesheet keys its transitions on. Three
 * hardenings change no outcome a single finger can reach: a `touchcancel` settles and never dismisses, a second finger
 * settles any live drag and disarms, and a drag brought back to 0 clears both the transform and the attribute. While
 * `enabled` is false (the prop absent, or a warm sheet closed) nothing is bound, written or read.
 */
/**
 * True when anything between the touch and the panel (the panel included) is scrolled down. The panel is not always
 * the scroller — a `scroll="inner"` sheet scrolls a body INSIDE it — so "at its top" is asked of the whole chain, or a
 * pull-down in a scrolled body would drag the sheet instead of scrolling back up.
 */
function scrolledUnder(target: EventTarget | null, panel: HTMLElement): boolean {
  for (let n = target instanceof Element ? target : null; n; n = n.parentElement) {
    if (n.scrollTop > 0) return true;
    if (n === panel) break;
  }
  return false;
}

export function useSheetDrag(
  panel: RefObject<HTMLElement | null>,
  { enabled, distance, onDismiss }: { enabled: boolean; distance: number; onDismiss: () => boolean | void },
): void {
  // Read through a ref so a fresh closure each render never rebinds the listeners mid-drag.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    const el = panel.current;
    if (!enabled || !el) return;
    let startY: number | null = null;
    let dy = 0;

    const clear = () => {
      el.style.transform = "";
      delete el.dataset.drag;
    };
    // Snap back: the transform goes, and `settling` carries the stylesheet's ease until the transition ends — at once
    // when nothing will transition (reduced motion's `transition: none` computes a 0s duration).
    const settle = () => {
      el.dataset.drag = "settling";
      el.style.transform = "";
      const duration = getComputedStyle(el).transitionDuration;
      if (duration && duration.split(",").every((d) => parseFloat(d) === 0)) delete el.dataset.drag;
    };
    // Only the panel's OWN transition ends the settle — a child's (a button's press) bubbles here too.
    const settled = (e: Event) => {
      if (e.target === el && el.dataset.drag === "settling") delete el.dataset.drag;
    };

    const start = (e: TouchEvent) => {
      if (startY !== null && dy > 0) settle(); // a second finger landing mid-drag never strands the panel
      startY = e.touches.length === 1 && !scrolledUnder(e.target, el) ? e.touches[0]!.clientY : null;
      dy = 0;
    };
    const move = (e: TouchEvent) => {
      if (startY === null) return;
      if (e.touches.length !== 1) {
        startY = null;
        if (dy > 0) settle();
        dy = 0;
        return;
      }
      dy = Math.max(0, e.touches[0]!.clientY - startY);
      if (dy > 0) {
        el.dataset.drag = "dragging";
        el.style.transform = `translateY(${dy}px)`;
      } else if (el.dataset.drag === "dragging") clear();
    };
    const end = () => {
      if (startY === null) return;
      const dismiss = dy > distance; // strictly greater (legacy `dragY > 110`)
      startY = null;
      // A dismissal can be REFUSED (the sheet holds edits and is asking first) — then the panel settles back rather
      // than staying where the finger left it.
      if (dismiss) {
        if (onDismissRef.current() === false) settle();
      } else if (dy > 0) settle();
      dy = 0;
    };
    const cancel = () => {
      if (startY === null) return;
      startY = null;
      if (dy > 0) settle();
      dy = 0;
    };

    const passive = { passive: true } as const;
    el.addEventListener("touchstart", start, passive);
    el.addEventListener("touchmove", move, passive);
    el.addEventListener("touchend", end, passive);
    el.addEventListener("touchcancel", cancel, passive);
    el.addEventListener("transitionend", settled);
    el.addEventListener("transitioncancel", settled);
    return () => {
      el.removeEventListener("touchstart", start);
      el.removeEventListener("touchmove", move);
      el.removeEventListener("touchend", end);
      el.removeEventListener("touchcancel", cancel);
      el.removeEventListener("transitionend", settled);
      el.removeEventListener("transitioncancel", settled);
      clear();
    };
  }, [panel, enabled, distance]);
}
